import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Loader2 } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/product/ProductCard';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function WishlistPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    fetch(`${API_BASE}/api/users/${user.id}/favorites`)
      .then(r => {
        if (!r.ok) throw new Error('fetch failed');
        return r.json();
      })
      .then(d => setItems(Array.isArray(d) ? d : []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [user?.id]);

  if (!isAuthenticated) {
    return (
      <Layout title="Wishlist">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-stone-300" />
          </div>
          <h2 className="text-lg font-semibold text-stone-900 mb-2">Sign in to see your wishlist</h2>
          <p className="text-stone-500 text-center mb-4">Tap the heart on any item to save it here</p>
          <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700" data-testid="wishlist-signin-btn">
            Sign In
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Wishlist">
      <div className="px-4 py-4" data-testid="wishlist-page">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-800 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20" data-testid="wishlist-error">
            <p className="text-stone-500 mb-3">Couldn't load your wishlist. Please try again.</p>
            <Button variant="outline" onClick={() => window.location.reload()}>Retry</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-4">
              <Heart className="w-8 h-8 text-stone-300" />
            </div>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">No saved items yet</h2>
            <p className="text-stone-500 text-center mb-4">Tap the heart on any item to save it here</p>
            <Button onClick={() => navigate('/browse')} className="bg-minted-600 hover:bg-minted-700" data-testid="wishlist-browse-btn">
              Browse Products
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-stone-500 mb-3" data-testid="wishlist-count">{items.length} saved {items.length === 1 ? 'item' : 'items'}</p>
            <div className="grid grid-cols-2 gap-4">
              {items.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
